import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeletePreAccountingProcessingSettingsMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, catchUnhandledDataGridActions, handleApiError} from '@dms/shared';

import {Nullish, composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['branch'] as const;

export function PreAccountingProcessingOverview() {
  const navigate = useNavigate();
  const navigateToNewDefinition = () => navigate(settingsRoutes.preAccountingProcessingNew);
  const navigateToDetail = (id: string) =>
    navigate(composePath(settingsRoutes.preAccountingProcessingDetail, {params: {id}}));

  const [deleteProcessingItem] = useDeletePreAccountingProcessingSettingsMutation();

  const handleDeleteProcessingItem = (
    id: string,
    branchName: string | Nullish,
    refreshData: () => void
  ) =>
    openDeleteDialog({
      onConfirm: () =>
        deleteProcessingItem({id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError),
      text: i18n.t('entity.accounting.labels.deleteDefinitionDeleteText', {
        name: branchName,
      }),
    });

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const id = isArray(rowId) ? head(rowId) : rowId;
    const branchName = getColumnData<{label: string}>('branch')?.value?.label;

    if (isNil(id)) {
      showNotification.error();
      return;
    }

    match(actionKey)
      .with('EDIT', () => navigateToDetail(id))
      .with('DETAIL', () => navigateToDetail(id))
      .with('DELETE', () => handleDeleteProcessingItem(id, branchName, refreshData))
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const title = i18n.t('page.accounting.labels.preProcessingCalculation');

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          actions: [
            {
              type: 'button',
              onClick: navigateToNewDefinition,
              title: i18n.t('page.accounting.actions.addDefinition'),
            },
          ],
        }}
        data-testid={testIds.settings.preAccountingProcessingOverview('settings-template')}
      >
        <DmsDataGrid
          gridCode="accounting-pre-accounting-processing-settings"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.preAccountingProcessingOverview('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
